import { call, put, select } from 'redux-saga/effects';
import axios from '../../../utils/axios';
import { BASEURL, TRACKER_API_BASE } from '../../../BaseUrl/BaseUrl';
import { isLogout } from '../../slices/login';
import {
  deleteCampaignSuccess,
  getCampaignByIdSuccess,
  getCampaignsSuccess,
  setTotalCampaigns,
  setCampaignListQuery,
  hasError as hasCampaignError,
  startLoading,
  stopLoading,
  startQrGeneration,
  failQrGeneration,
  setQrGenerationSuccess,
  startQrDownload,
  failQrDownload,
  finishQrDownload,
  startQrListLoading,
  setQrListSuccess,
  failQrList,
  startQrDetailLoading,
  setQrDetailSuccess,
  failQrDetail,
  setPaymentData,
  startPaymentBatchCsv,
  failPaymentBatchCsv,
  finishPaymentBatchCsv,
} from '../../slices/campaignSlice';
import {
  hasError,
  startFormLoading,
  stopFormLoading,
  getDeleteMessage,
  getErrorMessage,
  getSuccessMessage,
  setResponse,
} from '../../slices/responseStatus';

function authHeaders() {
  const Token = window.localStorage.getItem('auth-token');
  return { headers: { 'x-auth-token': `${Token}` } };
}

function getDeleteCampaignErrorMessage(error) {
  return error?.error?.message || error?.message || error?.meta?.message || '';
}

function triggerBrowserBlobDownload(blob, fileName) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export function* campaignTableSaga(state = {}) {
  try {
    yield put(hasError(false));
    yield put(hasCampaignError(false));
    yield put(startLoading(true));

    const { page = 1, limit = 20, search = '' } = state;
    const searchParam = search ? `&searchText=${encodeURIComponent(search)}` : '';

    const response = yield call(() =>
      axios.get(`${BASEURL}/campaigns?page=${page}&limit=${limit}${searchParam}`, authHeaders())
    );

    const { data, totalRowsValue } = response?.data?.data?.campaigns;
    yield put(getCampaignsSuccess(data));
    yield put(setTotalCampaigns(totalRowsValue));
    yield put(setCampaignListQuery({ page, limit, search: search || '' }));

    yield put(stopLoading());
  } catch (error) {
    yield put(hasError(true));
    yield put(hasCampaignError(true));
    yield put(stopLoading());
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load campaigns'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* getCampaignByIdSaga(state) {
  try {
    yield put(hasError(false));
    yield put(startFormLoading());

    const { id } = state;
    const response = yield call(() => axios.get(`${BASEURL}/campaigns/${id}`, authHeaders()));
    yield put(getCampaignByIdSuccess(response?.data?.data?.campaign?.[0] || null));
    yield put(stopFormLoading());
  } catch (error) {
    yield put(hasError(true));
    yield put(stopFormLoading());
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load campaign'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

function* refetchCampaignListLikeLastQuery() {
  const { listQuery } = yield select((s) => s.campaign);
  yield call(campaignTableSaga, {
    page: listQuery?.page ?? 1,
    limit: listQuery?.limit ?? 20,
    search: listQuery?.search ?? '',
  });
}

export function* addCampaignSaga(state) {
  try {
    yield put(hasError(false));
    yield put(startFormLoading());

    const { item } = state;
    const response = yield call(() => axios.post(`${BASEURL}/campaigns`, item, authHeaders()));

    yield put(getSuccessMessage(response?.data?.meta?.message || 'Campaign created'));
    yield put(setResponse(response.status));
    yield call(refetchCampaignListLikeLastQuery);
  } catch (error) {
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to create campaign'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* editCampaignSaga(state) {
  try {
    yield put(hasError(false));
    yield put(startFormLoading());

    const { item, id } = state;
    const response = yield call(() => axios.put(`${BASEURL}/campaigns/${id}`, item, authHeaders()));

    yield put(getSuccessMessage(response?.data?.meta?.message || 'Campaign updated'));
    yield put(setResponse(response.status));
    yield call(refetchCampaignListLikeLastQuery);
  } catch (error) {
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to update campaign'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* generateCampaignQrSaga(state) {
  try {
    yield put(hasError(false));
    yield put(startQrGeneration());

    const { campaignId, count } = state;
    const n = Number(count);

    if (!campaignId || !Number.isFinite(n) || n < 1) {
      yield put(failQrGeneration());
      yield put(getErrorMessage('Select a campaign and enter a valid count (1 or more).'));
      return;
    }

    const response = yield call(() =>
      axios.post(`${BASEURL}/qrs/campaign/${campaignId}?generate_qr_count=${encodeURIComponent(n)}`, {}, authHeaders())
    );

    const QRCodes = response?.data?.data?.QRCodes;
    const message = response?.data?.meta?.message || 'QR codes generated successfully';

    yield put(
      setQrGenerationSuccess({
        message,
        QRCodes,
      })
    );
  } catch (error) {
    yield put(failQrGeneration());
    yield put(hasError(true));
    yield put(
      getErrorMessage(
        error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to generate QR codes'
      )
    );

    if (error?.response?.status === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* downloadCampaignQrZipSaga(state) {
  try {
    yield put(hasError(false));
    yield put(startQrDownload());

    const { campaignId } = state;
    if (!campaignId) {
      yield put(failQrDownload());
      yield put(getErrorMessage('Select a campaign'));
      return;
    }

    const Token = window.localStorage.getItem('auth-token');
    const response = yield call(() =>
      axios.get(`${BASEURL}/qrs/download/campaign/${campaignId}`, {
        headers: { 'x-auth-token': `${Token}` },
        responseType: 'blob',
      })
    );

    const blob = response.data;
    const contentType = String(
      response.headers?.['content-type'] || response.headers?.['Content-Type'] || ''
    ).toLowerCase();

    if (contentType.includes('application/json')) {
      const text = yield call(() => blob.text());
      let msg = 'Failed to download QR codes';
      try {
        const json = JSON.parse(text);
        msg = json?.meta?.message || json?.error?.message || json?.message || msg;
      } catch (e) {
        /* ignore */
      }
      yield put(failQrDownload());
      yield put(hasError(true));
      yield put(getErrorMessage(msg));
      return;
    }

    const fileName = 'campaign-qrs.zip';
    triggerBrowserBlobDownload(blob, fileName);

    yield put(finishQrDownload({ toast: 'QR codes downloaded' }));
  } catch (error) {
    yield put(failQrDownload());
    yield put(hasError(true));
    let msg = 'Failed to download QR codes';
    if (error instanceof Blob) {
      try {
        const t = yield call(() => error.text());
        const j = JSON.parse(t);
        msg = j?.meta?.message || j?.error?.message || j?.message || msg;
      } catch (e) {
        /* keep default */
      }
    } else {
      msg = error?.error?.message || error?.message || error?.meta?.message || msg;
    }
    yield put(getErrorMessage(msg));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* deleteCampaignSaga(state) {
  const { id, forceDelete } = state;
  try {
    yield put(startLoading(true));
    yield put(hasError(false));

    const deleteOpts = { ...authHeaders() };
    if (forceDelete) {
      deleteOpts.params = { forceDelete: true };
    }
    const response = yield call(() => axios.delete(`${BASEURL}/campaigns/${id}`, deleteOpts));
    if (response.status === 200) {
      yield put(deleteCampaignSuccess(id));
    }

    yield call(refetchCampaignListLikeLastQuery);
    yield put(startLoading(false));
    yield put(getDeleteMessage(response?.data?.meta?.message || 'Campaign deleted'));
  } catch (error) {
    yield put(startLoading(false));
    if (error?.error?.statusCode === 401) {
      yield put(hasError(true));
      yield put(getErrorMessage(getDeleteCampaignErrorMessage(error) || 'Failed to delete campaign'));
      yield put(isLogout(true));
      return;
    }
    yield put(hasError(true));
    yield put(getErrorMessage(getDeleteCampaignErrorMessage(error) || 'Failed to delete campaign'));
  }
}

export function* fetchCampaignQrListSaga(action) {
  const { campaignId, page = 1, limit = 20, status, productId } = action;
  if (!campaignId) return;

  try {
    yield put(hasError(false));
    yield put(hasCampaignError(false));
    yield put(startQrListLoading());

    const params = [`limit=${encodeURIComponent(limit)}`, `page=${encodeURIComponent(page)}`];
    if (typeof status === 'string' && status.length > 0) {
      params.push(`status=${encodeURIComponent(status)}`);
    }
    const productIdTrim = typeof productId === 'string' ? productId.trim() : '';
    if (productIdTrim.length > 0) {
      params.push(`productId=${encodeURIComponent(productIdTrim)}`);
    }
    const q = params.join('&');
    const response = yield call(() => axios.get(`${BASEURL}/qrs/campaign/${campaignId}?${q}`, authHeaders()));

    const qrBlock = response?.data?.data?.QRCode;
    const rows = Array.isArray(qrBlock?.result) ? qrBlock.result : [];
    const total = Number(qrBlock?.totalRowsValue ?? 0);

    yield put(setQrListSuccess({ rows, total }));
  } catch (error) {
    yield put(failQrList());
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load campaign QR list'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* getQrByCodeSaga(action) {
  const { qrCode } = action;
  if (!qrCode) return;

  try {
    yield put(hasError(false));
    yield put(hasCampaignError(false));
    yield put(startQrDetailLoading());

    const encoded = encodeURIComponent(qrCode);
    const response = yield call(() => axios.get(`${BASEURL}/qrs/${encoded}`, authHeaders()));

    const raw = response?.data?.data?.QRCode;
    const record = Array.isArray(raw) ? raw[0] : raw;

    yield put(setQrDetailSuccess(record || null));
  } catch (error) {
    yield put(failQrDetail());
    yield put(hasError(true));
    yield put(getErrorMessage(error?.error?.message || error?.message || 'Failed to load QR details'));
    if (error?.error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* getPaymentsSaga() {
  try {
    yield put(hasError(false));

    const response = yield call(() => axios.get(`${BASEURL}/payments`, authHeaders()));

    const paymentData = response?.data?.data?.data || [];
    yield put(setPaymentData(paymentData));
  } catch (error) {
    yield put(hasError(true));
    if (error?.response?.status === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* downloadPaymentBatchCsvSaga(action) {
  const { campaignId } = action;
  if (!campaignId) {
    yield put(failPaymentBatchCsv());
    yield put(hasError(true));
    yield put(getErrorMessage('Select a campaign to download CSV'));
    return;
  }

  try {
    yield put(hasError(false));
    yield put(hasCampaignError(false));
    yield put(startPaymentBatchCsv());

    const csvMetaRes = yield call(() =>
      axios.get(`${BASEURL}/payments/campaigns/${campaignId}/download-csv`, authHeaders())
    );

    const meta = csvMetaRes?.data?.data;
    const filePath = meta?.filePath;
    const fileName = meta?.fileName || `campaign_${campaignId}_payments.csv`;
    if (!filePath) {
      throw new Error(csvMetaRes?.data?.message || 'Failed to get CSV file URL');
    }

    try {
      const blobRes = yield call(() => axios.get(filePath, { responseType: 'blob' }));
      const blob = blobRes?.data;
      const ct = String(blobRes?.headers?.['content-type'] || blobRes?.headers?.['Content-Type'] || '').toLowerCase();
      if (ct.includes('application/json') && blob instanceof Blob) {
        const text = yield call(() => blob.text());
        let msg = 'Failed to download CSV file';
        try {
          const json = JSON.parse(text);
          msg = json?.message || json?.error?.message || msg;
        } catch (e) {
          /* ignore */
        }
        throw new Error(msg);
      }
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid CSV file response');
      }
      triggerBrowserBlobDownload(blob, fileName);
    } catch (blobErr) {
      if (typeof window !== 'undefined') {
        window.open(filePath, '_blank', 'noopener,noreferrer');
      }
      yield put(
        finishPaymentBatchCsv({
          toast: 'CSV opened in a new tab — save the file from there if it did not download automatically.',
        })
      );
      return;
    }

    yield put(
      finishPaymentBatchCsv({
        toast: csvMetaRes?.data?.message || 'CSV downloaded',
      })
    );
  } catch (error) {
    yield put(failPaymentBatchCsv());
    yield put(hasError(true));
    const msg = error?.error?.message || error?.message || error?.meta?.message || 'Failed to download payment CSV';
    yield put(getErrorMessage(msg));
    if (error?.error?.statusCode === 401 || error?.statusCode === 401) {
      yield put(isLogout(true));
    }
  }
}

export function* getcustomFarmerReportSaga() {
  try {
    const addRes = yield call(() => axios.get(`${TRACKER_API_BASE}/farmer-stats`, authHeaders()));

    const result = addRes.data;
    if (result?.data?.fileUrl) {
      const link = document.createElement('a');
      link.href = result.data.fileUrl;
      link.download = result.data.fileName || 'farmer_analytics.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    yield put({
      payload: error?.response?.data?.message || error.message || 'Something went wrong',
    });
  }
}
